import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { clearCache } from "@/lib/api";
import { applyLang } from "@/i18n";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=user
  const [ready, setReady] = useState(false);
  const [serverDown, setServerDown] = useState(false);

  // Solo un rifiuto vero (4xx) chiude la sessione: se il server è lento o in avvio resti collegato.
  const refresh = useCallback(
    () => api.get("/auth/me").then(({ data }) => { setServerDown(false); setUser(data); }).catch((e) => {
      const st = e.response?.status;
      if (st && st < 500) {
        localStorage.removeItem("token");
        setUser(false);
      } else setServerDown(true);
    }),
    []
  );

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      setUser(false);
      setReady(true);
      return;
    }
    refresh().finally(() => setReady(true));
  }, [refresh]);

  useEffect(() => {
    window.addEventListener("trial-expired", refresh);
    return () => window.removeEventListener("trial-expired", refresh);
  }, [refresh]);

  const startSession = (data) => {
    clearCache();
    localStorage.setItem("token", data.token);
    const local = localStorage.getItem("lang");
    if (local) api.patch("/auth/preferences", { lang: local }).catch(() => {});
    else if (data.user.lang) applyLang(data.user.lang);
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password }, { retryable: true });
    return startSession(data);
  };

  const registerCompany = async (payload) => {
    const { data } = await api.post("/auth/register-company", payload);
    return startSession(data);
  };

  const setOnboarding = async (patch) => {
    setUser((u) => (u ? { ...u, onboarding: { ...(u.onboarding || {}), ...patch } } : u));
    await api.patch("/auth/onboarding", patch).catch(() => {});
  };

  const logout = () => {
    localStorage.removeItem("token");
    clearCache();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, ready, serverDown, login, registerCompany, logout, refresh, setOnboarding }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
